import React, { useRef, useState } from 'react';
import './ImageUpload.css';

// A focused target only: pasting into text fields or a different image slot
// must never accidentally upload an image elsewhere on the page.
export function ClipboardImageTarget({ children, onFiles, testid, disabled, multiple = false }) {
  const target = useRef(null);
  const pending = useRef(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  async function accept(files) {
    if (disabled || pending.current) return;
    const images = Array.from(files || []).filter(f => f.type.startsWith('image/'));
    if (!images.length) { setMessage('No image found. Copy an image, then paste here.'); return; }
    const chosen = multiple ? images : images.slice(0, 1);
    if (chosen.some(f => f.size > 20 * 1024 * 1024)) { setMessage('Images must be 20 MB or smaller.'); return; }
    pending.current = true; setBusy(true); setMessage('');
    try {
      await onFiles(chosen.map((f, i) => new File([f], `pasted-image-${Date.now()}-${i}.${f.type.split('/')[1].replace('jpeg', 'jpg').replace('svg+xml', 'svg')}`, { type: f.type })));
    } catch (e) { setMessage(e.response?.data?.detail || e.message || 'Image could not be added. Please try again.'); }
    finally { pending.current = false; setBusy(false); }
  }
  function paste(e) {
    if (e.target.closest('input, textarea, [contenteditable="true"]')) return;
    const images = Array.from(e.clipboardData?.items || []).filter(it => it.type.startsWith('image/')).map(it => it.getAsFile()).filter(Boolean);
    if (!images.length) return; // Normal text clipboard behaviour is untouched.
    e.preventDefault(); e.stopPropagation(); accept(images);
  }
  async function readClipboard() {
    target.current?.focus();
    if (!navigator.clipboard?.read) { setMessage('Press Ctrl+V or ⌘V here to paste your image.'); return; }
    try {
      const items = await navigator.clipboard.read();
      const images = [];
      for (const item of items) {
        const type = item.types.find(t => t.startsWith('image/'));
        if (type) images.push(await item.getType(type));
      }
      await accept(images);
    } catch (_) { setMessage('Clipboard access is unavailable. Press Ctrl+V or ⌘V here, or use Upload.'); target.current?.focus(); }
  }
  return <div ref={target} className="image-paste-target" tabIndex={disabled ? -1 : 0} data-testid={testid + '-paste-target'}
    aria-label="Image upload and paste" aria-busy={busy || !!disabled} onPaste={paste}
    onDragOver={e => { if (!disabled && Array.from(e.dataTransfer.types).includes('Files')) e.preventDefault(); }}
    onDrop={e => { e.preventDefault(); e.stopPropagation(); accept(e.dataTransfer.files); }}>
    {children}
    <button type="button" className="btn btn-sm image-paste-button" data-testid={testid + '-paste'} disabled={disabled || busy}
      title="Paste an image from your clipboard" onClick={readClipboard}>{busy ? 'Adding image…' : 'Paste image'}</button>
    {message && <div className="image-paste-message" role="status" data-testid={testid + '-paste-message'}>{message}</div>}
  </div>;
}

export function FileUpload({ testid, onFiles, label = 'Upload', accept = 'image/*', multiple = false, disabled, className = 'btn' }) {
  const input = useRef(null);
  return <ClipboardImageTarget testid={testid} disabled={disabled} onFiles={onFiles} multiple={multiple}>
    <button type="button" className={className} data-testid={testid} disabled={disabled} onClick={() => input.current?.click()}>{label}</button>
    <input ref={input} type="file" accept={accept} multiple={multiple} disabled={disabled} style={{ display: 'none' }} data-testid={testid + '-input'}
      onChange={e => { const files = Array.from(e.target.files); e.target.value = ''; if (files.length) onFiles(files); }} />
  </ClipboardImageTarget>;
}

export function ImageUpload({ url, onFile, onClear, testid, inline = false }) {
  const input = useRef(null);
  return <ClipboardImageTarget testid={testid} onFiles={files => onFile(files[0])}>
    {url && <div className={inline ? 'img-inline' : 'img-slot filled'} data-testid={testid + '-preview'}>
      <img src={url} alt="Uploaded artwork" data-testid={testid + '-image'} />
      <button type="button" className="btn btn-sm btn-danger" data-testid={testid + '-clear'} title="Remove image" onClick={onClear}>{inline ? '✕' : 'Remove'}</button>
    </div>}
    <button type="button" className={url ? 'btn btn-sm' : (inline ? 'img-inline empty' : 'img-slot empty')}
      data-testid={testid + '-upload'} onClick={() => input.current?.click()}>{url ? 'Replace image' : 'Upload image'}</button>
    <input ref={input} type="file" accept="image/*" style={{ display: 'none' }} data-testid={testid + '-input'}
      onChange={e => { const file = e.target.files[0]; e.target.value = ''; if (file) onFile(file); }} />
  </ClipboardImageTarget>;
}